
import * as React from 'react';
import { observer } from 'mobx-react';
import { TransitionGroup } from 'react-transition-group';

import { IWebGivingMainViewModel } from '../main/webgiving-main-view-model';
import { ModalType } from './modal-type';
import { HelpModalContent, HelpModalHeader } from './help-modal/help-modal';
import { IWebGivingAppState } from '../webgiving-app-state';
import { buildLinkDetails } from '../utils/kiosk-link';
import { GiftType } from '../webgiving-payment-state';
import { WebGivingPage } from '../pages';
import { Modal, ModalHeader, ModalContentsHtml, ModalContents } from '../components/modal/modal';
import { MobileSignInInfoModalContent, MobileSignInInfoModalHeader } from './mobile-sign-in-info-modal/mobile-sign-in-info-modal';
import { WebGivingMerchant, WebGivingPushpayInfo } from '../webgiving-generated';
import { ErrorInfo, getErrorInfo } from '../utils/error-utils';
import { Text } from '../components/text';
import { getLanguageWithFallback } from '../utils/language-utils';

@observer
export class ModalCommodore extends React.Component<{ vm: IWebGivingMainViewModel, appState: IWebGivingAppState, onError: (errorInfo: ErrorInfo, customData?: any) => void }> {
	private documentActiveElement: Element | null;

	render() {
		const { vm, appState } = this.props;
		const { viewData: { MerchantInfo, PushpayInfo }, hasTranslationEnabled } = vm;
		const { paymentState } = appState;
		const kioskLinkDetails = buildLinkDetails(MerchantInfo, paymentState);
		let content: React.ReactElement<any>[] | null = null;

		switch (vm.visibleModal) {
			case ModalType.NeedHelp:
				content = [
					<HelpModalHeader />,
					<HelpModalContent
						isRecurringPayment={paymentState.giftType === GiftType.Recurring}
						kioskLinkDetails={kioskLinkDetails}
						paymentLabel={MerchantInfo.PaymentLabel}
						showResendSecurityCode={vm.currentPage !== WebGivingPage.EnterMobileNumber}
						resendCodeDisabled={vm.resendCodeDisabled.get()}
						onRequestClose={this.handleClose}
						pushpayInfo={PushpayInfo} />,
				];
				break;

			case ModalType.MobileSignInInfo:
				content = [
					<MobileSignInInfoModalHeader />,
					<MobileSignInInfoModalContent
						kioskLinkDetails={kioskLinkDetails}
						pushpayInfo={PushpayInfo} />,
				];
				break;

			case ModalType.MerchantPrivacy:
				content = MerchantPrivacy(MerchantInfo);
				break;

			case ModalType.MerchantTerms:
				content = MerchantTerms(MerchantInfo);
				break;

			case ModalType.PushpayPrivacy:
				content = PushpayPrivacy(PushpayInfo, hasTranslationEnabled);
				break;

			case ModalType.PushpayTerms:
				content = PushpayTerms(PushpayInfo, hasTranslationEnabled);
				break;

			case ModalType.FailedToResolveFund:
				content = FailedToResolveFunds(MerchantInfo);
				break;

			case ModalType.InvalidPreconfiguredLink:
				content = InvalidPreconfiguredLink(MerchantInfo);
				break;

			case null:
				break;

			default:
				const unsupported: never = vm.visibleModal;
				throw new Error(`ModalType ${unsupported} is unsupported`);
		}

		if (content) {
			// ensure keys are assigned
			content = content.map((x, i) => React.cloneElement(x, { key: i }));
		}

		return (
			<TransitionGroup>
				{content && <Modal key={typeof vm.visibleModal !== 'number' ? '' : vm.visibleModal}
					onClose={this.handleClose}
					onEnter={this.setDocumentActiveElement}
					onExited={this.returnFocus}>
					{content}
				</Modal>}
			</TransitionGroup>
		);
	}

	componentDidMount() {
		this.setupGlobalEventHandlers();
	}

	componentDidUpdate() {
		this.setupGlobalEventHandlers();
	}

	componentDidCatch(error: any, info: React.ErrorInfo) {
		this.props.vm.closeModal();
		this.props.onError(getErrorInfo(error), info);
	}

	private setupGlobalEventHandlers() {
		const anyModalVisible = typeof this.props.vm.visibleModal === 'number';

		if (anyModalVisible) {
			document.body.style.overflow = 'hidden';
			document.documentElement.style.overflow = 'hidden';
			document.body.addEventListener('touchmove', this.preventTouchMove, false);
			document.body.addEventListener('keydown', this.keyDown);
		} else {
			document.body.style.overflow = '';
			document.documentElement.style.overflow = '';
			document.body.removeEventListener('touchmove', this.preventTouchMove, false);
			document.body.removeEventListener('keydown', this.keyDown);
		}
	}

	private keyDown = (evt: any) => {
		if ((evt.key === 'Escape' || evt.keyCode === 27)) {
			this.handleClose();
		}
	}

	private preventTouchMove(ev: TouchEvent) {
		ev.preventDefault();
	}

	private handleClose = () => {
		this.props.vm.closeModal();
	}

	private setDocumentActiveElement = () => {
		this.documentActiveElement = document.activeElement;
	}

	private returnFocus = () => {
		if (this.documentActiveElement && this.documentActiveElement instanceof HTMLElement) {
			this.documentActiveElement.focus();
		}
	}
}

export const PushpayPrivacy = ({ PrivacyStatement, PrivacyStatementMultiLang }: WebGivingPushpayInfo, hasTranslationEnabled?: boolean) => {
	if (NewFeatures.WebGiving_S3Document_MultiLang) {
		const language = getLanguageWithFallback(hasTranslationEnabled);
		return [
			<ModalHeader key="modal-header" text="Privacy Policy" />,
			<ModalContentsHtml key="modal-content" className="brand-html-content" contentBase64={PrivacyStatementMultiLang[language.TwoLetterIsoCode]} />
		];
	}

	return [
		<ModalHeader key="modal-header" text="Privacy Policy" />,
		<ModalContentsHtml key="modal-content" className="brand-html-content" contentBase64={PrivacyStatement} />,
	];
};

export const MerchantPrivacy = ({ TradingName, PrivacyPolicy }: WebGivingMerchant) => {
	return [
		<ModalHeader key="modal-header" params={{company: TradingName}} text={`Privacy Policy for {{company}}`} />,
		<ModalContentsHtml key="modal-content" className="brand-html-content" contentBase64={PrivacyPolicy} />,
	];
};

export const PushpayTerms = ({ TermsAndConditions, TermsAndConditionsMultiLang }: WebGivingPushpayInfo, hasTranslationEnabled?: boolean) => {
	if (NewFeatures.WebGiving_S3Document_MultiLang) {
		const language = getLanguageWithFallback(hasTranslationEnabled);
		return [
			<ModalHeader key="modal-header" text="Terms and Conditions" />,
			<ModalContentsHtml key="modal-content" className="brand-html-content" contentBase64={TermsAndConditionsMultiLang[language.TwoLetterIsoCode]} />
		];
	}
	
	return [
		<ModalHeader key="modal-header" text="Terms and Conditions" />,
		<ModalContentsHtml key="modal-content" className="brand-html-content" contentBase64={TermsAndConditions} />,
	];
};

export const MerchantTerms = ({ TradingName, TermsAndConditions }: WebGivingMerchant) => {
	return [
		<ModalHeader key="modal-header" params={{company: TradingName}} text={`Terms and Conditions for {{company}}`} />,
		<ModalContentsHtml key="modal-content" className="brand-html-content" contentBase64={TermsAndConditions} />,
	];
};

export const FailedToResolveFunds = ({ TradingName, PaymentLabel }: WebGivingMerchant) => {
	const action = PaymentLabel.VerbLowerCase;
	const company = TradingName;

	return [
		<ModalHeader key="modal-header" text="Invalid Preconfigured Fund" />,
		<ModalContents key="modal-content">
			<div className="brand-html-content">
				<Text params={{action, company}}>{`The fund you have been directed to is not valid. Please select another fund to {{action}} to and contact an administrator at {{company}}.`}</Text>
			</div>
		</ModalContents>,
	];
};

export const InvalidPreconfiguredLink = ({ TradingName, PaymentLabel }: WebGivingMerchant) => {
	const company = TradingName;
	const titleAction = PaymentLabel.ActionSentenceCase;
	const contentAction = PaymentLabel.ActionLowerCase;

	return [
		<ModalHeader key="modal-header" params={{action: titleAction}} text={`Invalid {{action}} Link`} />,
		<ModalContents key="modal-content">
			<div className="brand-html-content">
				<Text params={{action: contentAction, company}}>{`The {{action}} link is not valid.  Please contact an administrator at {{company}}.`}</Text>
			</div>
		</ModalContents>,
	];
};
